import { formatCurrency } from '@/src/utils/currency/formatCurrency'
import type { HolidayNavTab, HolidayPrice, HolidaySection } from '@/src/types/holidays'

export const HOLIDAYS_BASE_PATH = '/holidays'

/** Holidays nav entries (toolbar, header, home tabs): the brand flag is on and the host is cleartrip.ae.
 *  'AE' is only ever Cleartrip AE (Flyin AE is 'FAE'), the one domain with holiday content. */
export const isHolidaysNavEnabled = (holidaysFeature: boolean | undefined, domainCountry: string | undefined) =>
    !!holidaysFeature && domainCountry === 'AE'

export const buildCollectionHref = (collectionSlug: string) => `${HOLIDAYS_BASE_PATH}/${collectionSlug}`

export const buildPackageHref = (collectionSlug: string, packageSlug: string) =>
    `${HOLIDAYS_BASE_PATH}/${collectionSlug}/${packageSlug}`

/** Where the Holidays nav entries land (locale-less): the seasonal collection the RN home tile also opens. */
export const HOLIDAYS_NAV_PATH = buildCollectionHref('uae-national-day-holiday-packages')

/** Set by the native app's WebView; the app then owns the page chrome. */
export const HOLIDAYS_APP_PARAM = 'isApp'

// Carries app mode onto in-flow links so every page the WebView reaches stays chrome-free.
export const withHolidayAppMode = (href: string, isApp: boolean) =>
    isApp ? `${href}?${HOLIDAYS_APP_PARAM}=true` : href

/**
 * Package prices are authored as whole units in the package's own currency — they are a quote, not
 * a fare, so there is no conversion and no precision to preserve.
 */
export const formatHolidayPrice = (price: HolidayPrice, locale: string): string =>
    formatCurrency({ amount: price.amount, currency: price.currency, locale, precision: 0 })

/** Sticky nav tabs, derived from the sections so the tab bar cannot drift from the page body. */
export const buildHolidayNavTabs = (sections: HolidaySection[]): HolidayNavTab[] =>
    sections
        .filter(section => section.showInNav !== false && Boolean(section.navLabel ?? section.title))
        .map(section => ({ id: section.id, label: (section.navLabel ?? section.title) as string }))

// Trip dates are calendar dates, so they are built and formatted in UTC to never shift a day.
const toUtcDate = (value: string) => {
    const [year, month, day] = value.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day))
}

/**
 * "1 - 6 December 2026", "28 November - 5 December 2026" or, across years, both full dates.
 * Day-month order in English matches the authored copy; Arabic uses its own month names and digits.
 */
export const formatHolidayTripWindow = (startDate: string, endDate: string, locale: string): string => {
    const start = toUtcDate(startDate)
    const end = toUtcDate(endDate)
    const bcp47 = locale === 'ar' ? 'ar' : 'en-GB'
    const format = (date: Date, options: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(bcp47, { ...options, timeZone: 'UTC' }).format(date)

    const endLabel = format(end, { day: 'numeric', month: 'long', year: 'numeric' })
    const sameYear = start.getUTCFullYear() === end.getUTCFullYear()

    if (sameYear && start.getUTCMonth() === end.getUTCMonth()) {
        return `${format(start, { day: 'numeric' })} - ${endLabel}`
    }
    if (sameYear) {
        return `${format(start, { day: 'numeric', month: 'long' })} - ${endLabel}`
    }
    return `${format(start, { day: 'numeric', month: 'long', year: 'numeric' })} - ${endLabel}`
}

/**
 * Page width for the holidays surface, matched to the parent site.
 *
 * Measured on musafir.com at both 1440px and 1920px viewports: its content box is 1248px wide and
 * centred. MUI's Container is border-box and adds 24px gutters either side, so the constant is
 * 1248 + 48 to land on the same 1248px of content.
 *
 * Container's named sizes are no use here anyway — this theme redefines the breakpoints, so `lg`
 * is 1024px and `xl` is 1280px.
 */
export const HOLIDAYS_CONTENT_WIDTH = 1248
export const HOLIDAYS_MAX_WIDTH = HOLIDAYS_CONTENT_WIDTH + 48

/**
 * Collection banner box shape, matched to the creatives (web 4000×1000, mobile 750×602) so the
 * full-bleed banner scales with the viewport instead of a fixed height cropping its headline.
 */
export const HOLIDAYS_BANNER_ASPECT_RATIO = {
    DESKTOP: '4 / 1',
    MOBILE: '750 / 602'
} as const

/**
 * Corner radii for the holidays surface, on the theme's shape scale (1 unit = 8px).
 *
 * `SURFACE` is 12px, matching the SRP "modify search" bar
 * (`desktop/srp/searchHeader/searchModifierInputs/inputsContainer`), which is the reference for
 * every card-level container here: the nav bar, the content card, the enquiry card and the
 * package cards. `NESTED` is a step smaller so rows and tables sit inside those without their
 * corners fighting the container's.
 */
export const HOLIDAYS_RADIUS = {
    /** 12px — card-level containers. */
    SURFACE: 1.5,
    /** 8px — rows, tables and other boxes nested inside a surface. */
    NESTED: 1
} as const
